import { setAuthTenant } from './firebase'

export interface TenantInfo {
  slug: string
  display_name: string
  idp_tenant_id: string
}

export interface AuthConfig {
  /** The auth master switch. false = dev/no-auth; true = apex or tenant host. */
  enabled: boolean
  base_domain: string
  /** Non-null only on a resolved, initialised tenant host. */
  tenant: TenantInfo | null
}

/**
 * Result of loading `GET /api/auth/config`. A discriminated union so the app can
 * branch on the bootstrap outcome without inspecting HTTP status codes:
 *   ok           → config resolved (dev / apex / tenant)
 *   not_found    → 404: the subdomain maps to no known tenant
 *   initialising → 409: the tenant IdP is being provisioned (retried, still busy)
 *   error        → 503 (IdP not configured) or any other/network failure
 */
export type AuthConfigResult =
  | { status: 'ok'; config: AuthConfig }
  | { status: 'not_found' }
  | { status: 'initialising' }
  | { status: 'error' }

let cachedConfig: AuthConfig | null = null

const MAX_ATTEMPTS = 3

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Fetch the public bootstrap config. On a 409 ("tenant is initialising") retries
 * up to 3 times with a linear backoff before giving up as `initialising`. When a
 * tenant is resolved, its IdP tenant id is pinned onto the Firebase auth instance
 * immediately (must happen on every page load, before the auth gate renders).
 */
export async function loadAuthConfig(): Promise<AuthConfigResult> {
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    try {
      const res = await fetch('/api/auth/config')

      if (res.status === 404) {
        return { status: 'not_found' }
      }
      if (res.status === 409) {
        if (attempt < MAX_ATTEMPTS - 1) {
          await delay(500 * (attempt + 1))
          continue
        }
        return { status: 'initialising' }
      }
      if (res.status === 503 || !res.ok) {
        return { status: 'error' }
      }

      const config = (await res.json()) as AuthConfig
      cachedConfig = config
      if (config.tenant) {
        setAuthTenant(config.tenant.idp_tenant_id)
      }
      return { status: 'ok', config }
    } catch {
      if (attempt < MAX_ATTEMPTS - 1) {
        await delay(500 * (attempt + 1))
        continue
      }
      return { status: 'error' }
    }
  }
  return { status: 'error' }
}

export function getAuthConfig(): AuthConfig | null {
  return cachedConfig
}

/**
 * Host role, derived from the resolved config rather than parsing the URL:
 * no config or auth disabled → dev; a resolved tenant → tenant; otherwise apex.
 */
export function getHostRole(): 'apex' | 'tenant' | 'dev' {
  const config = cachedConfig
  if (!config || !config.enabled) {
    return 'dev'
  }
  return config.tenant ? 'tenant' : 'apex'
}

export function getOwnSlug(): string | null {
  return cachedConfig?.tenant?.slug ?? null
}
